import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ConfirmDialogService } from '../../../services/confirm-dialog-service/confirm-dialog.service';
import {
    OrderdirectPurchaseInvoice,
    OrderdirectPurchaseInvoiceService,
} from '../../../services/orderdirect-purchase-invoice/orderdirect-purchase-invoice.service';

@Component({
    selector: 'app-orderdirect-purchase-invoice-detail',
    templateUrl: './orderdirect-purchase-invoice-detail.component.html',
    styleUrls: ['./orderdirect-purchase-invoice-detail.component.scss'],
    standalone: false,
})
export class OrderdirectPurchaseInvoiceDetailComponent implements OnInit, OnDestroy {
    public invoice: OrderdirectPurchaseInvoice | null = null;
    public loading = false;
    private subscriptions = new Subscription();
    public jsonData: any = null;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private invoiceService: OrderdirectPurchaseInvoiceService,
        private confirmDialog: ConfirmDialogService
    ) {}

    ngOnInit() {
        const id = this.route.snapshot.params['id'];
        if (id) {
            this.loadInvoice(id);
        }
    }

    loadInvoice(id: number) {
        this.loading = true;
        this.subscriptions.add(
            this.invoiceService.getInvoiceById(id).subscribe({
                next: response => {
                    this.invoice = response.data;
                    this.jsonData =
                        typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
                    this.loading = false;
                },
                error: error => {
                    console.error('Error loading purchase invoice:', error);
                    this.confirmDialog.confirm({
                        title: 'Fout',
                        text: 'Fout bij laden van inkoopfactuur',
                        confirm: 'Ok',
                        cancel: null,
                    });
                    this.loading = false;
                    this.router.navigate(['/orderdirect-inkoopfacturen']);
                },
            })
        );
    }

    sendToAfas() {
        if (!this.invoice) return;

        if (this.invoice.afas_ornu) {
            this.confirmDialog.confirm({
                title: 'Fout',
                text: 'Deze inkoopfactuur is al naar AFAS verzonden',
                confirm: 'Ok',
                cancel: null,
            });
            return;
        }

        if (this.invoice.execute_at && !this.invoice.failed_at) {
            this.confirmDialog.confirm({
                title: 'Fout',
                text: 'Deze inkoopfactuur wordt al verwerkt',
                confirm: 'Ok',
                cancel: null,
            });
            return;
        }

        this.confirmDialog
            .confirm({
                title: 'Naar AFAS verzenden',
                text: `Weet u zeker dat u inkoopfactuur ${this.invoice.od_bon_nr} naar AFAS wilt verzenden?`,
                confirm: 'Ja',
                cancel: null,
            })
            .then(() => {
                this.subscriptions.add(
                    this.invoiceService.sendToAfas(this.invoice.id).subscribe({
                        next: response => {
                            this.confirmDialog.confirm({
                                title: 'Succes',
                                text: response.message,
                                confirm: 'Ok',
                                cancel: null,
                            });
                            this.loadInvoice(this.invoice.id);
                        },
                        error: error => {
                            console.error('Error sending to AFAS:', error);
                            this.confirmDialog.confirm({
                                title: 'Fout',
                                text: 'Fout bij verzenden naar AFAS: ' + (error.error?.message || error.message),
                                confirm: 'Ok',
                                cancel: null,
                            });
                        },
                    })
                );
            })
            .catch(() => {
                // User cancelled
            });
    }

    resetStatus() {
        if (!this.invoice) return;

        this.confirmDialog
            .confirm({
                title: 'Status resetten',
                text: `Weet u zeker dat u de status van inkoopfactuur ${this.invoice.od_bon_nr} wilt resetten? Alle AFAS velden worden gewist.`,
                confirm: 'Ja',
                cancel: null,
            })
            .then(() => {
                this.subscriptions.add(
                    this.invoiceService.resetStatus(this.invoice.id).subscribe({
                        next: response => {
                            this.loadInvoice(this.invoice.id);
                        },
                        error: error => {
                            console.error('Error resetting status:', error);
                            this.confirmDialog.confirm({
                                title: 'Fout',
                                text: 'Fout bij resetten status: ' + (error.error?.message || error.message),
                                confirm: 'Ok',
                                cancel: null,
                            });
                        },
                    })
                );
            })
            .catch(() => {
                // User cancelled
            });
    }

    canSendToAfas(): boolean {
        if (!this.invoice) return false;
        return !this.invoice.afas_ornu && !(this.invoice.execute_at && !this.invoice.failed_at);
    }

    getStatusClass(status: string): string {
        switch (status) {
            case 'completed':
                return 'badge-success';
            case 'pending':
                return 'badge-warning';
            case 'failed':
                return 'badge-danger';
            case 'new':
                return 'badge-info';
            default:
                return 'badge-secondary';
        }
    }

    getStatusLabel(status: string): string {
        switch (status) {
            case 'completed':
                return 'Voltooid';
            case 'pending':
                return 'In behandeling';
            case 'failed':
                return 'Mislukt';
            case 'new':
                return 'Nieuw';
            default:
                return status;
        }
    }

    goBack() {
        this.router.navigate(['/orderdirect-inkoopfacturen']);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
